import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "./ui/button";
import { FileText, Download, RefreshCw, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DocRow({ label, dossierId, endpoint, testId }) {
  const [generated, setGenerated] = useState(false);
  const [generating, setGenerating] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/justificante/${dossierId}/${endpoint}/status`);
      if (res.ok) {
        const data = await res.json();
        setGenerated(data.generated);
      }
    } catch {}
  }, [dossierId, endpoint]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/justificante/${dossierId}/${endpoint}/generate`, { method: "POST" });
      if (!res.ok) throw new Error();
      toast.success(`${label} generado`);
      setGenerated(true);
    } catch {
      toast.error(`Error al generar ${label}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await axios.get(`${API}/justificante/${dossierId}/${endpoint}/download`, { responseType: 'blob', validateStatus: (s) => s < 500 });
      if (res.status === 404) {
        toast.error(`${label} no generado.`);
        return;
      }
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const disposition = res.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      link.href = url;
      link.download = filenameMatch ? filenameMatch[1].trim() : `${testId}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 200);
    } catch {
      toast.error(`Error al descargar ${label}`);
    }
  };

  return (
    <div className="flex items-center gap-2 p-2 rounded-lg border bg-slate-50" data-testid={`${testId}-row`}>
      <FileText className="w-4 h-4 text-slate-500 shrink-0" />
      <span className="text-sm font-medium text-slate-700 flex-1 min-w-0 truncate">{label}</span>
      {generated && (
        <Button variant="outline" size="sm" onClick={handleDownload} data-testid={`download-${testId}`} title="Descargar PDF">
          <Download className="w-4 h-4" />
        </Button>
      )}
      <Button
        variant={generated ? "ghost" : "default"}
        size="sm"
        disabled={generating}
        onClick={handleGenerate}
        data-testid={`generate-${testId}`}
        title={generated ? "Re-generar" : "Generar"}
      >
        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
      </Button>
    </div>
  );
}

export default function JustificanteSection({ dossierId }) {
  return (
    <div className="space-y-1">
      <DocRow label="Justificante Cesión" dossierId={dossierId} endpoint="justificante" testId="justificante" />
      <DocRow label="Plantilla Entrega CAE" dossierId={dossierId} endpoint="plantilla" testId="plantilla" />
    </div>
  );
}
